import api from '../../../api';
import { compactFieldSchema, initiativeFromGenerate } from './mapper';
import {
  AccuracyFinding,
  FieldFill,
  FieldValue,
  GfdManifest,
  GfdPlaybook,
  ReadinessItem,
  ResearchPacket,
  SeedInitiative,
} from './types';

function modelId(): string | undefined {
  try {
    const saved = localStorage.getItem('selectedLLM');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.id) return parsed.id;
    }
  } catch {
    /* keep default */
  }
  return undefined;
}

export async function generateGfdPacket(args: {
  files: File[];
  notes: string;
  playbook: GfdPlaybook;
  championName?: string;
  championTitle?: string;
  sessionId?: number | null;
}): Promise<SeedInitiative & { sessionId?: number }> {
  const form = new FormData();
  args.files.forEach((file) => form.append('files', file));
  form.append('notes', args.notes || '');
  form.append('championName', args.championName || '');
  form.append('championTitle', args.championTitle || '');
  form.append('fields', JSON.stringify(compactFieldSchema(args.playbook)));
  form.append('playbookVersion', args.playbook.version || '1.2');
  if (args.sessionId) form.append('sessionId', String(args.sessionId));
  const mid = modelId();
  if (mid) form.append('modelId', mid);

  const res = await api.post('/api/gfd-intake/generate', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  });
  const data = res.data?.data;
  if (!data) throw new Error(res.data?.error || 'Generate failed');

  const initiative = initiativeFromGenerate({
    id: data.sessionId ? `gfd-${data.sessionId}` : undefined,
    name: data.initiativeName,
    summary: data.summary,
    notes: args.notes,
    championName: args.championName,
    championTitle: args.championTitle,
    documents: data.documents,
    fills: data.fills,
  });
  return { ...initiative, sessionId: data.sessionId };
}

export async function listGfdSessions(): Promise<{ sessions: any[]; totalCount: number }> {
  const res = await api.get('/api/gfd-intake/sessions');
  return { sessions: res.data.sessions || [], totalCount: res.data.totalCount || 0 };
}

export async function loadGfdSession(id: number) {
  const res = await api.get(`/api/gfd-intake/sessions/${id}`);
  return res.data.session;
}

export async function deleteGfdSession(id: number) {
  await api.delete(`/api/gfd-intake/sessions/${id}`);
}

export async function saveGfdSession(body: Record<string, unknown>) {
  const res = await api.post('/api/gfd-intake/sessions', body);
  return res.data as { sessionId: number; session: any };
}

export async function patchGfdFills(sessionId: number, fills: Array<{ fieldId: string; value: FieldValue; state?: string }>) {
  const res = await api.patch(`/api/gfd-intake/sessions/${sessionId}/fills`, { fills });
  return res.data.fills as FieldFill[];
}

export async function patchReadiness(sessionId: number, readiness: ReadinessItem[]) {
  const res = await api.patch(`/api/gfd-intake/sessions/${sessionId}/readiness`, { readiness });
  return res.data.readiness as ReadinessItem[];
}

export async function runGfdInterview(sessionId: number, messages: { role: string; content: string }[], manifest: GfdManifest) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/interview`, { messages, manifest }, { timeout: 180000 });
  return res.data as { message: { role: string; content: string }; manifest: GfdManifest };
}

export async function runGfdSimulation(sessionId: number, manifest: GfdManifest, iterations = 10000) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/simulate`, { manifest, iterations }, { timeout: 120000 });
  return res.data as { simResults: any; manifest: GfdManifest };
}

export async function applyGfdSimulation(sessionId: number, percentile: 50 | 80) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/apply-simulation`, { percentile });
  return res.data as { fills: FieldFill[]; percentile: number };
}

export async function runGfdResearch(sessionId: number, query: string, gfdFieldId?: string) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/research`, { query, gfdFieldId }, { timeout: 60000 });
  return res.data as { packet: ResearchPacket; manifest: GfdManifest; fills: FieldFill[] };
}

export async function runGfdAccuracy(sessionId: number, playbook: GfdPlaybook) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/accuracy`, { fields: compactFieldSchema(playbook) });
  return res.data.report as { findings: AccuracyFinding[]; unresolvedRequired: string[]; exportBlocked: boolean; generated_at: string };
}

export async function decideGfdFinding(sessionId: number, findingId: string, decision: 'approve' | 'reject' | 'adjust', value?: FieldValue) {
  const res = await api.post(`/api/gfd-intake/sessions/${sessionId}/accuracy/${encodeURIComponent(findingId)}`, { decision, value });
  return res.data as { fills: FieldFill[]; findings: AccuracyFinding[] };
}

async function downloadBlob(path: string, body: object, fallbackName: string) {
  const res = await api.post(path, body, { responseType: 'blob', timeout: 120000 });
  const blob = new Blob([res.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const disposition = res.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="([^"]+)"/);
  a.download = match ? match[1] : fallbackName;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadWalkthroughDocx(sessionId: number, playbook: GfdPlaybook, name: string) {
  await downloadBlob(
    `/api/gfd-intake/sessions/${sessionId}/export/walkthrough-docx`,
    { playbook },
    `gfd-walkthrough-${name}.docx`
  );
}

export async function downloadBusinessCaseDocx(sessionId: number, name: string) {
  await downloadBlob(
    `/api/gfd-intake/sessions/${sessionId}/export/business-case-docx`,
    {},
    `gfd-business-case-${name}.docx`
  );
}

export async function downloadBusinessCaseXlsx(sessionId: number, name: string) {
  await downloadBlob(
    `/api/gfd-intake/sessions/${sessionId}/export/business-case-xlsx`,
    {},
    `gfd-business-case-${name}.xlsx`
  );
}
